import type { ChildControls, ChildProfile, KidsSummary } from "@orbin/contracts";
import { ageBandFor } from "@orbin/contracts";
import type { KidsApi } from "./api.js";

/** In-memory stand-in for the kids router, used until it ships and in the visual fixtures. */
export function createMemoryKidsApi(
  options: { planId?: "start" | "plus"; withProfiles?: boolean; year?: number } = {},
): KidsApi {
  const year = options.year ?? 2026;
  const plan = options.planId ?? "plus";
  const stamp = "2026-11-02T12:00:00.000Z";
  const seed: ChildProfile[] =
    options.withProfiles === false
      ? []
      : [
          {
            id: "c1",
            name: "Lia",
            ageBand: "6-9",
            avatarId: "onca",
            colorKey: "#F59E0B",
            createdAt: stamp,
            archivedAt: null,
          },
          {
            id: "c2",
            name: "Théo",
            ageBand: "10-12",
            avatarId: "coruja",
            colorKey: "#14B8A6",
            createdAt: stamp,
            archivedAt: null,
          },
        ];
  let profiles = seed;
  let pinSet = profiles.length > 0;
  let pin = "123456";
  let paired = "";
  const controls = new Map<string, ChildControls>();
  const controlsFor = (profileId: string): ChildControls => {
    const found = controls.get(profileId);
    if (found) return found;
    const band = profiles.find((p) => p.id === profileId)?.ageBand ?? "6-9";
    const fresh: ChildControls = {
      profileId,
      version: 1,
      features: { computer: false, web: false, routines: false, voice: false, homeworkPhoto: true },
      dailyLimitMinutes: band === "6-9" ? 60 : band === "10-12" ? 90 : 120,
      quietHours: { start: "20:00", end: "07:00" },
      visibility: band === "6-9" || band === "10-12" ? "full" : "summary",
      contentLevel: band === "6-9" || band === "10-12" ? "kids" : "teen",
      directAnswerSubjects: [],
      allowedSkillIds: [],
    };
    controls.set(profileId, fresh);
    return fresh;
  };
  const limit = plan === "plus" ? 2 : 0;
  const summary = (): KidsSummary => ({
    kidsEnabled: true,
    plan: {
      planId: plan,
      planName: plan === "plus" ? "Plus" : "Start",
      childProfilesLimit: limit,
      childProfilesUsed: profiles.length,
      canCreate: profiles.length < limit,
      upgradeTo: plan === "start" ? "plus" : null,
    },
    profiles,
    pinSet,
    pinLockedUntil: null,
    timezoneSet: true,
    parentAgeMethod: plan === "plus" ? "attestation_paid_plan" : null,
  });
  let acknowledged: string | null = null;
  let activityCleared = false;
  let alertDeleted = false;
  let reportDeleted = false;
  let attempts = 5;
  return {
    summary: async () => summary(),
    profile: {
      create: async (input) => {
        const band = ageBandFor(input.birthYear, year);
        if (!band) throw Object.assign(new Error("band"), { code: "invalid_age" });
        if (profiles.length >= limit)
          throw Object.assign(new Error("limit"), { data: { code: "plan_child_limit", limit } });
        if (input.pin) {
          pin = input.pin;
          pinSet = true;
        }
        const created: ChildProfile = {
          id: "c" + (profiles.length + 10),
          name: input.name,
          ageBand: band,
          avatarId: input.avatarId,
          colorKey: input.colorKey,
          createdAt: stamp,
          archivedAt: null,
        };
        profiles = [...profiles, created];
        return created;
      },
      delete: async ({ profileId, confirmName }) => {
        if (profiles.find((p) => p.id === profileId)?.name !== confirmName) throw new Error("name");
        profiles = profiles.filter((p) => p.id !== profileId);
        return { ok: true };
      },
    },
    pin: {
      verify: async (input) => {
        if (input.pin === pin) {
          attempts = 5;
          return { ok: true };
        }
        attempts -= 1;
        if (attempts <= 0)
          throw Object.assign(new Error("locked"), { data: { code: "pin_locked" } });
        return { ok: false, attemptsLeft: attempts, lockedUntil: null };
      },
      reset: async (input) => {
        pin = input.pin;
        attempts = 5;
        return { ok: true };
      },
    },
    pairing: {
      create: async () => ({
        code: "482915",
        expiresAt: new Date(Date.now() + 600_000).toISOString(),
      }),
      redeem: async ({ code }) => {
        if (code !== "482915")
          throw Object.assign(new Error("pairing"), { data: { code: "pairing_invalid" } });
        paired = profiles[0]?.id ?? "c1";
        return {
          token: "t".repeat(40),
          profileId: paired,
          botId: "bot-" + paired,
          expiresAt: new Date(Date.now() + 12 * 3_600_000).toISOString(),
        };
      },
    },
    homework: {
      upload: async () =>
        controlsFor(paired || "c1").features.homeworkPhoto
          ? { status: "accepted", photoId: "p-homework" }
          : { status: "disabled" },
    },
    me: async () => {
      const profile = profiles.find((p) => p.id === paired) ?? profiles[0]!;
      const own = controlsFor(profile.id);
      return {
        profile,
        botId: "bot-" + profile.id,
        features: own.features,
        dailyLimitMinutes: own.dailyLimitMinutes,
        usedTodayMinutes: 0,
        quietNow: false,
        visibility: own.visibility,
      };
    },
    session: {
      start: async ({ profileId, pin: given }) => {
        if (given !== pin) throw Object.assign(new Error("pin"), { data: { code: "pin_wrong" } });
        return {
          token: "t".repeat(40),
          profileId,
          botId: "bot-" + profileId,
          expiresAt: new Date(Date.now() + 12 * 3_600_000).toISOString(),
        };
      },
    },
    controls: {
      get: async ({ profileId }) => controlsFor(profileId),
      update: async ({ profileId, expectedVersion, patch }) => {
        const current = controlsFor(profileId);
        if (current.version !== expectedVersion)
          throw Object.assign(new Error("conflict"), { data: { code: "controls_conflict" } });
        const next: ChildControls = {
          ...current,
          ...patch,
          features: { ...current.features, ...(patch.features ?? {}) },
          version: current.version + 1,
        };
        controls.set(profileId, next);
        return next;
      },
    },
    activity: {
      clear: async () => {
        activityCleared = true;
        return { ok: true as const };
      },
      list: async ({ profileId }) => activityCleared ? { visibility: controlsFor(profileId).visibility, items: [] } : ({
        visibility: controlsFor(profileId).visibility,
        items: [
          {
            id: "a1",
            at: stamp,
            kind: "chat",
            subject: "matematica",
            minutes: 18,
            text: "Frações: como somar",
            summary: null,
          },
          {
            id: "a2",
            at: stamp,
            kind: "chat",
            subject: "ciencias",
            minutes: 6,
            text: "Por que o céu é azul?",
            summary: null,
          },
          {
            id: "a3",
            at: stamp,
            kind: "chat",
            subject: "portugues",
            minutes: 12,
            text: "Ditado de palavras",
            summary: null,
          },
        ],
      }),
    },
    alerts: {
      list: async () => ({
        items: alertDeleted ? [] : [
          {
            id: "al1",
            profileId: "c1",
            at: stamp,
            kind: "crisis",
            severity: "critical",
            category: "self_harm",
            acknowledgedAt: acknowledged,
          },
        ],
      }),
      delete: async () => {
        if (acknowledged === null) throw new Error("alert_not_acknowledged");
        alertDeleted = true;
        return { ok: true as const };
      },
      acknowledge: async ({ alertId }) => {
        acknowledged = stamp;
        return {
          id: alertId,
          profileId: "c1",
          at: stamp,
          kind: "crisis",
          severity: "critical",
          category: "self_harm",
          acknowledgedAt: stamp,
        };
      },
    },
    report: {
      delete: async () => {
        reportDeleted = true;
        return { ok: true as const };
      },
      list: async () => ({
        items: reportDeleted ? [] : [
          {
            id: "r1",
            profileId: "c1",
            periodStart: "2026-10-26T00:00:00.000Z",
            periodEnd: "2026-11-01T23:59:00.000Z",
            totalMinutes: 252,
            days: [40, 65, 30, 80, 55, 20, 45].map((minutes, index) => ({
              date: index < 6 ? "2026-10-" + (26 + index) : "2026-11-01",
              minutes,
            })),
            subjects: [
              { subject: "matematica", count: 5 },
              { subject: "ciencias", count: 2 },
            ],
            safetyAlertCount: 0,
            summaries: null,
          },
        ],
      }),
    },
  };
}
